import React, { useState, useEffect, useRef } from 'react';
import {
  PieChart, Pie, Cell,
  BarChart, Bar,
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  FiEdit2, FiDownload, FiFilter, FiX, FiPlus,
  FiSend, FiUpload, FiChevronDown, FiSave
} from 'react-icons/fi';
import SubNav from '../components/subNav';
import SEO from '../components/SEO';
import { useParams, useNavigate } from 'react-router-dom';
import { baseUrl } from '../config';

/* ─────────────────────────────────────────────────────────────────────────────
   Animation styles (kept as-is from original)
───────────────────────────────────────────────────────────────────────────── */
const animationStyles = `
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background: linear-gradient(to bottom right, #eff6ff, #e0e7ff);
  }
  .loading-spinner {
    width: 80px;
    height: 80px;
    border: 8px solid #e0e7ff;
    border-top-color: #6366f1;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
  .loading-text  { margin-top: 24px; font-size: 22px; font-weight: 600; color: #6366f1; text-align: center; }
  .loading-sub   { margin-top: 8px;  font-size: 15px; color: #64748b;   text-align: center; }
`;

/* ─────────────────────────────────────────────────────────────────────────────
   Animated background circles  (positions pre-computed to avoid hydration mismatch)
───────────────────────────────────────────────────────────────────────────── */
const BG_CIRCLES = [
  { w: 120, h: 90, l: 12, t: 8 },
  { w: 80, h: 110, l: 28, t: 35 },
  { w: 150, h: 70, l: 55, t: 15 },
  { w: 100, h: 130, l: 70, t: 50 },
  { w: 90, h: 85, l: 85, t: 25 },
  { w: 110, h: 95, l: 40, t: 65 },
  { w: 130, h: 75, l: 5, t: 70 },
  { w: 75, h: 120, l: 60, t: 75 },
  { w: 95, h: 100, l: 20, t: 55 },
  { w: 140, h: 85, l: 78, t: 40 },
  { w: 85, h: 115, l: 33, t: 85 },
  { w: 105, h: 90, l: 90, t: 68 },
  { w: 115, h: 80, l: 48, t: 30 },
  { w: 70, h: 105, l: 15, t: 42 },
  { w: 125, h: 95, l: 65, t: 82 },
  { w: 88, h: 88, l: 52, t: 58 },
  { w: 98, h: 112, l: 8, t: 20 },
  { w: 78, h: 98, l: 37, t: 10 },
  { w: 108, h: 78, l: 75, t: 5 },
  { w: 118, h: 92, l: 22, t: 78 },
];

const AnimatedBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    {BG_CIRCLES.map((c, i) => (
      <div
        key={i}
        className="absolute rounded-full bg-blue-500 opacity-5"
        style={{ width: c.w, height: c.h, left: `${c.l}%`, top: `${c.t}%` }}
      />
    ))}
  </div>
);

/* ─────────────────────────────────────────────────────────────────────────────
   Helper
───────────────────────────────────────────────────────────────────────────── */
const formatName = (name) =>
  name
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, s => s.toUpperCase())
    .trim();

const INR = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

const CHART_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088fe', '#00C49F', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD'];

/* ─────────────────────────────────────────────────────────────────────────────
   Default / empty budget shape (shown before first AI prediction)
───────────────────────────────────────────────────────────────────────────── */
const EMPTY_BUDGET = {
  total: 0, spent: 0, remaining: 0,
  categories: {},
  breakdown: {},
  confidence: 0
};

/* ═════════════════════════════════════════════════════════════════════════════
   Main Component
═════════════════════════════════════════════════════════════════════════════ */
const Budget = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  /* ── auth / permission ── */
  const [activeUser, setActiveUser] = useState(0);
  const [isCheckingExisting, setIsChecking] = useState(true);

  /* ── budget conversation ── */
  const [conversationId, setConversationId] = useState(null);
  const [budgetReady, setBudgetReady] = useState(false);   // true once first AI message received
  const [budgetData, setBudgetData] = useState(EMPTY_BUDGET);

  /* ── setup flow ── */
  // 'pipeline_prompt' | 'manual_input' | 'done'
  const [setupMode, setSetupMode] = useState('pipeline_prompt');
  const [manualInput, setManualInput] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);

  /* ── chat ── */
  const [chatMessages, setChatMessages] = useState([
    {
      sender: 'bot',
      text: '👋 Hi! I can generate a detailed budget estimate for your project. Would you like me to use the script you already created in the pipeline, or would you like to provide a different one?'
    }
  ]);
  const [prompt, setPrompt] = useState('');
  const [isSending, setIsSending] = useState(false);
  const chatEndRef = useRef(null);

  /* ── chart / filter ── */
  const [chartType, setChartType] = useState('pie');
  const [selectedCategory, setSelectedCategory] = useState('total');
  const [showFilterMenu, setShowFilterMenu] = useState(false);

  /* ── category editing ── */
  const [editingCategory, setEditingCategory] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [inputValues, setInputValues] = useState({});

  /* ── upload modal ── */
  const [uploadShown, setUploadShown] = useState(false);
  const fileInputRef = useRef(null);

  /* ─────────────────────────────────────────────────────────────────────────
     Permission + existing budget check
  ───────────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    checkProjectPermissions();
  }, []);

  useEffect(() => {
    if (activeUser && projectId) {
      checkExistingBudget();
    }
  }, [activeUser, projectId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  async function checkProjectPermissions() {
    try {
      // getUserById instead of getUserIDByToken - same single call, but its
      // response also includes user_type at no extra round trip, so the
      // free-tier check below can be skipped entirely for non-students
      // instead of always paying for a /free-tier/status call to learn that.
      const tokenRes = await fetch(`${baseUrl}/getUserById`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (!tokenRes.ok) { navigate('/login'); return; }

      const { user_id, user_type } = await tokenRes.json();
      setActiveUser(user_id);

      const membersRes = await fetch(`${baseUrl}/getMembersByProjectId`, {
        headers: { project_id: projectId }
      });
      if (!membersRes.ok) { window.alert('Failed to check project permissions'); navigate('/'); return; }

      const members = await membersRes.json();
      if (!members.map(m => m.user_id).includes(user_id)) {
        window.alert('This user does not have access to this project!');
        navigate('/');
        return;
      }

      // Free-tier bypass: a currently-eligible verified student skips the
      // subscription check below entirely - checked independently via
      // GET /free-tier/status (same pattern StudentToolSelector.jsx already
      // uses) rather than trusted from navigation state. Only called for
      // user_type === 'student' - a non-student never pays for this extra
      // round trip at all. Anyone else (non-student, or a student who isn't
      // currently eligible - expired, etc.) falls through to the exact
      // existing check below, unchanged.
      if (user_type === 'student') {
        const freeTierRes = await fetch(`${baseUrl}/free-tier/status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        if (freeTierRes.ok) {
          const freeTierStatus = await freeTierRes.json();
          if (freeTierStatus.state === 'active' || freeTierStatus.state === 'not_started') {
            return;
          }
        }
      }

      const toolsRes = await fetch(`${baseUrl}/getAvailableTools`, {
        headers: {
          project_id: projectId,
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (!toolsRes.ok) { window.alert('Failed to check tool permissions'); navigate('/services'); return; }

      const tools = await toolsRes.json();
      if (!tools.map(t => t.name).includes('Budgeting')) {
        window.alert("You haven't purchased this tool yet!");
        navigate('/services');
      }

    } catch (err) {
      window.alert('Error checking permissions');
      navigate('/');
    }
  }

  async function checkExistingBudget() {
    setIsChecking(true);
    try {
      const res = await fetch(
        `${baseUrl}/budgetConversation/getConversationHistory?project_id=${projectId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
      );
      if (!res.ok) throw new Error('Failed to fetch history');

      const data = await res.json();
      const convs = data.conversations || [];

      if (convs.length > 0) {
        // Load most recent budget conversation
        await loadBudgetConversation(convs[0].id);
      } else {
        // No existing budget — show setup prompt
        setIsChecking(false);
      }
    } catch (err) {
      setIsChecking(false);
    }
  }

  async function loadBudgetConversation(convId) {
    try {
      const res = await fetch(
        `${baseUrl}/budgetConversation/getConversation?conversation_id=${convId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } }
      );
      if (!res.ok) throw new Error('Failed to load conversation');

      const data = await res.json();

      // Rebuild chat messages
      const msgs = data.messages.map(m => ({
        sender: m.message_type === 'user' ? 'user' : 'bot',
        text: m.content,
        budgetData: m.budget_data || null
      }));
      setChatMessages(msgs.length > 0 ? msgs : chatMessages);

      // Find latest AI message with budget data
      const latestAI = [...data.messages].reverse().find(m => m.message_type === 'ai' && m.budget_data);
      if (latestAI && latestAI.budget_data) {
        applyBudgetData(latestAI.budget_data);
      }

      setConversationId(convId);
      setBudgetReady(true);
      setSetupMode('done');
    } catch (err) {
    } finally {
      setIsChecking(false);
    }
  }

  function applyBudgetData(bd) {
    setBudgetData({
      total: bd.total || 0,
      spent: bd.spent || 0,
      remaining: bd.remaining || 0,
      categories: bd.categories || {},
      breakdown: bd.breakdown || {},
      confidence: bd.confidence || 0
    });
    // Seed input values for edit fields
    const vals = { spent: bd.spent || 0 };
    Object.entries(bd.categories || {}).forEach(([k, v]) => { vals[k] = v.value; });
    setInputValues(vals);
    setBudgetReady(true);
  }

  /* ─────────────────────────────────────────────────────────────────────────
     Setup flow: pipeline vs manual
  ───────────────────────────────────────────────────────────────────────── */
  async function handlePipelineChoice() {
    addUserMsg('✅ Yes, use the pipeline script');
    addBotMsg('Fetching your script and generating budget prediction...');
    setIsSending(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 45000);

    try {
      const res = await fetch(
        `${baseUrl}/budgetConversation/generateFromPipeline?project_id=${projectId}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
          signal: controller.signal
        }
      );

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Pipeline generation failed');
      }

      const result = await res.json();
      onBudgetGenerated(result);

    } catch (err) {
      addBotMsg(`❌ ${err.message}. You can provide a script manually instead.`);
      setSetupMode('manual_input');
    } finally {
      clearTimeout(timeoutId);
      setIsSending(false);
    }
  }

  function handleManualChoice() {
    addUserMsg('🔧 I\'ll provide my own script');
    addBotMsg('Sure! You can type your script directly in the chat below, or upload a PDF file using the upload button. I\'ll analyze it and generate a detailed budget.');
    setSetupMode('manual_input');
  }

  async function handleManualSubmit() {
    if (!manualInput.trim() && !uploadedFile) return;

    const userText = uploadedFile ? `📄 Uploaded: ${uploadedFile.name}` : manualInput.trim();
    addUserMsg(userText);
    addBotMsg('Analyzing your script and generating budget...');
    setIsSending(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 45000);

    try {
      const formData = new FormData();
      formData.append('project_id', projectId);

      if (uploadedFile) {
        // First upload file, then send URL
        const uploadRes = await fetch(`${baseUrl}/upload/direct`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
          body: (() => {
            const fd = new FormData();
            fd.append('file', uploadedFile);
            fd.append('file_type', 'document');
            fd.append('user_id', activeUser);
            return fd;
          })()
        });
        if (!uploadRes.ok) throw new Error('File upload failed');
        const uploadResult = await uploadRes.json();
        formData.append('script_file_url', uploadResult.public_url);
      } else {
        formData.append('script_text', manualInput.trim());
      }

      if (conversationId) formData.append('conversation_id', conversationId);

      const res = await fetch(`${baseUrl}/budgetConversation/generateFromManual`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        body: formData,
        signal: controller.signal
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Manual generation failed');
      }

      const result = await res.json();
      onBudgetGenerated(result);

    } catch (err) {
      addBotMsg(`❌ ${err.message}. Please try again.`);
    } finally {
      clearTimeout(timeoutId);
      setManualInput('');
      setUploadedFile(null);
      setIsSending(false);
    }
  }

  function onBudgetGenerated(result) {
    setConversationId(result.conversation_id);
    applyBudgetData(result.budget_data);
    setSetupMode('done');
    // Replace last "generating..." bot message with the real explanation
    setChatMessages(prev => {
      const updated = [...prev];
      for (let i = updated.length - 1; i >= 0; i--) {
        if (updated[i].sender === 'bot') {
          updated[i] = { sender: 'bot', text: result.message_content, budgetData: result.budget_data };
          break;
        }
      }
      return updated;
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     Chat prompt (once budget is live)
  ───────────────────────────────────────────────────────────────────────── */
  async function sendPrompt() {
    if (!prompt.trim() || isSending) return;
    const text = prompt.trim();
    setPrompt('');
    addUserMsg(text);
    addBotMsg('Processing your request...');
    setIsSending(true);

    // Simple keyword detection for common intents
    const lower = text.toLowerCase();

    if (lower.includes('update') || lower.includes('change') || lower.includes('set')) {
      // Try to extract category name and value
      const catKeys = Object.keys(budgetData.categories);
      const matchedCat = catKeys.find(k => lower.includes(k.toLowerCase()) || lower.includes(formatName(k).toLowerCase()));
      const valueMatch = text.match(/[\d,]+/);
      const value = valueMatch ? parseFloat(valueMatch[0].replace(/,/g, '')) : null;

      if (matchedCat && value !== null) {
        await applyUpdateCategory(matchedCat, value, text);
        return;
      }
    }

    if (lower.includes('add') && (lower.includes('expense') || lower.includes('spent') || lower.includes('cost'))) {
      const valueMatch = text.match(/[\d,]+/);
      if (valueMatch) {
        const amount = parseFloat(valueMatch[0].replace(/,/g, ''));
        await applyUpdateSpent(amount, text);
        return;
      }
    }

    // Fallback — inform user what actions are available
    replaceBotMsg(
      `I can help you with:\n• Update a category (e.g. "Update casting to 2000000")\n• Add an expense (e.g. "Add expense 500000")\n• Add a new category using the "+" button below\n• Edit any category directly by clicking the edit icon.\n\nCould you be more specific about what you'd like to change?`
    );
    setIsSending(false);
  }

  /* ─────────────────────────────────────────────────────────────────────────
     CRUD actions → backend
  ───────────────────────────────────────────────────────────────────────── */
  async function applyUpdateCategory(categoryName, newValue, userMsg = null) {
    try {
      const res = await fetch(`${baseUrl}/budgetConversation/updateCategory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          category_name: categoryName,
          new_value: newValue,
          message: userMsg
        })
      });
      if (!res.ok) throw new Error('Update failed');
      const result = await res.json();
      applyBudgetData(result.budget_data);
      replaceBotMsg(result.message_content);
    } catch (err) {
      replaceBotMsg(`❌ ${err.message}`);
    } finally {
      setIsSending(false);
      setEditingCategory(null);
    }
  }

  async function applyAddCategory(categoryName, value, description = null) {
    try {
      const res = await fetch(`${baseUrl}/budgetConversation/addCategory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          category_name: categoryName,
          value,
          description,
          message: `Add new category: ${categoryName}`
        })
      });
      if (!res.ok) throw new Error('Add category failed');
      const result = await res.json();
      applyBudgetData(result.budget_data);
      setChatMessages(prev => [...prev,
      { sender: 'bot', text: result.message_content, budgetData: result.budget_data }
      ]);
    } catch (err) {
      setChatMessages(prev => [...prev, { sender: 'bot', text: `❌ ${err.message}` }]);
    } finally {
      setEditingCategory(null);
    }
  }

  async function applyUpdateSpent(amountToAdd, userMsg = null) {
    try {
      const res = await fetch(
        `${baseUrl}/budgetConversation/updateSpent?conversation_id=${conversationId}&amount_to_add=${amountToAdd}${userMsg ? `&message=${encodeURIComponent(userMsg)}` : ''}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        }
      );
      if (!res.ok) throw new Error('Update spent failed');
      const result = await res.json();
      applyBudgetData(result.budget_data);
      replaceBotMsg(result.message_content);
    } catch (err) {
      replaceBotMsg(`❌ ${err.message}`);
    } finally {
      setIsSending(false);
      setEditingCategory(null);
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
     UI edit handlers (local optimistic + API save)
  ───────────────────────────────────────────────────────────────────────── */
  // ✅ FIX
  function handleEdit(category) {
    setEditingCategory(category);
    if (category === 'spent') {
      setInputValues(prev => ({ ...prev, spent: budgetData.spent }));
    } else if (category === 'spent-add') {
      setInputValues(prev => ({ ...prev, 'spent-add': '' }));
    } else if (category === 'new-category') {
      // ✅ Initialize both fields explicitly
      setInputValues(prev => ({
        ...prev,
        'new-category-name': '',
        'new-category-value': ''
      }));
    } else if (budgetData.categories[category]) {
      setInputValues(prev => ({ ...prev, [category]: budgetData.categories[category].value }));
    }
  }

  // ✅ FIX — detect text vs number fields
  function handleInputChange(category, value) {
    const isTextField = category === 'new-category-name';
    setInputValues(prev => ({
      ...prev,
      [category]: isTextField ? value : (parseFloat(value) || 0)
    }));
  }

  async function handleSaveEdit(category) {
    if (!conversationId) { setEditingCategory(null); return; }
    const value = parseFloat(String(inputValues[category]).replace(/,/g, '')) || 0;

    if (category === 'spent') {
      const diff = value - budgetData.spent;
      if (diff !== 0) await applyUpdateSpent(diff, `Set total spent to ${INR(value)}`);
      else setEditingCategory(null);
    } else {
      await applyUpdateCategory(category, value);
    }
  }

  async function handleAddExpense() {
    if (!conversationId) { setEditingCategory(null); return; }
    const amount = parseFloat(inputValues['spent-add']) || 0;
    if (amount > 0) await applyUpdateSpent(amount);
    else setEditingCategory(null);
  }

  async function handleAddCategory() {
    if (!conversationId) { setEditingCategory(null); return; }
    const name = (inputValues['new-category-name'] || '').trim();
    const value = parseFloat(String(inputValues['new-category-value']).replace(/,/g, '')) || 0;
    if (!name) {
      alert('Please enter a category name');
      return;
    }
    if (value <= 0) {
      alert('Please enter a budget amount greater than 0');
      return;
    }
    await applyAddCategory(name, value);
  }

  /* ─────────────────────────────────────────────────────────────────────────
     Chat helpers
  ───────────────────────────────────────────────────────────────────────── */
  function addUserMsg(text) {
    setChatMessages(prev => [...prev, { sender: 'user', text }]);
  }
  function addBotMsg(text) {
    setChatMessages(prev => [...prev, { sender: 'bot', text }]);
  }
  function replaceBotMsg(text) {
    setChatMessages(prev => {
      const updated = [...prev];
      for (let i = updated.length - 1; i >= 0; i--) {
        if (updated[i].sender === 'bot') { updated[i] = { ...updated[i], text }; break; }
      }
      return updated;
    });
  }

  /* ─────────────────────────────────────────────────────────────────────────
     Chart data
  ───────────────────────────────────────────────────────────────────────── */
  function generateChartData() {
    if (selectedCategory === 'total') {
      return Object.entries(budgetData.categories).map(([name, d]) => ({
        name: formatName(name), value: d.value
      }));
    }
    const v = budgetData.categories[selectedCategory]?.value || 0;
    return [
      { name: 'Primary', value: Math.round(v * 0.6) },
      { name: 'Secondary', value: Math.round(v * 0.3) },
      { name: 'Contingency', value: Math.round(v * 0.1) }
    ];
  }

  function renderChart() {
    const data = generateChartData();
    if (!data.length) {
      return <div className="flex items-center justify-center h-60 text-gray-400">No data yet — generate a budget first.</div>;
    }

    const fmt = (v) => INR(v);

    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={v => `₹${(v / 100000).toFixed(0)}L`} />
              <Tooltip formatter={fmt} />
              <Legend />
              <Bar dataKey="value" fill="#6366f1" />
            </BarChart>
          </ResponsiveContainer>
        );
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={v => `₹${(v / 100000).toFixed(0)}L`} />
              <Tooltip formatter={fmt} />
              <Legend />
              <Line type="monotone" dataKey="value" stroke="#6366f1" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        );
      case 'hist':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={v => `₹${(v / 100000).toFixed(0)}L`} />
              <Tooltip formatter={fmt} />
              <Legend />
              <Bar dataKey="value" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        );
      default: // pie
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data}
                cx="50%" cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={90}
                dataKey="value"
              >
                {data.map((_, i) => (
                  <Cell key={`cell-${i}`} fill={CHART_COLORS[i % CHART_COLORS.length]} />  // ✅ Cell not cell
                ))}
              </Pie>
              <Tooltip formatter={fmt} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );
    }
  }

  /* ─────────────────────────────────────────────────────────────────────────
     Setup chat panel (shown when no budget yet)
  ───────────────────────────────────────────────────────────────────────── */
  function renderSetupChat() {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 flex flex-col h-[500px]">
        <h2 className="text-xl font-semibold text-indigo-700 mb-4">Budget Setup</h2>

        {/* Messages */}
        <div className="flex-1 bg-gray-50 rounded-lg p-4 mb-4 overflow-y-auto space-y-3">
          {chatMessages.map((msg, i) => (
            <div key={i} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-xs px-4 py-2 rounded-lg text-sm whitespace-pre-wrap ${msg.sender === 'user'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-200 text-gray-800'
                  }`}
              >
                {msg.text}
              </div>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>

        {/* Setup action buttons */}
        {setupMode === 'pipeline_prompt' && !isSending && (
          <div className="flex gap-3">
            <button
              onClick={handlePipelineChoice}
              className="flex-1 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
            >
              ✅ Yes, use pipeline script
            </button>
            <button
              onClick={handleManualChoice}
              className="flex-1 py-2 border border-indigo-400 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-50 transition-colors"
            >
              🔧 Use a different script
            </button>
          </div>
        )}

        {setupMode === 'manual_input' && (
          <div className="space-y-2">
            {uploadedFile && (
              <div className="flex items-center bg-indigo-50 px-3 py-2 rounded-lg text-sm text-indigo-700">
                <FiUpload className="mr-2" />
                {uploadedFile.name}
                <button onClick={() => setUploadedFile(null)} className="ml-auto text-red-400 hover:text-red-600">
                  <FiX />
                </button>
              </div>
            )}
            <div className="relative">
              <textarea
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 h-20 resize-none text-sm"
                placeholder="Paste your script here, or upload a PDF..."
                value={manualInput}
                onChange={e => setManualInput(e.target.value)}
                disabled={isSending}
              />
              <div className="absolute bottom-2 right-2 flex gap-1">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="p-1.5 bg-indigo-100 rounded-full text-indigo-700 hover:bg-indigo-200"
                  title="Upload PDF"
                >
                  <FiUpload size={15} />
                </button>
                <button
                  onClick={handleManualSubmit}
                  disabled={isSending || (!manualInput.trim() && !uploadedFile)}
                  className="p-1.5 bg-indigo-600 rounded-full text-white hover:bg-indigo-700 disabled:opacity-40"
                >
                  <FiSend size={15} />
                </button>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.txt,.doc,.docx"
              onChange={e => setUploadedFile(e.target.files?.[0] || null)}
            />
          </div>
        )}

        {isSending && (
          <div className="flex items-center justify-center py-3 text-indigo-600 text-sm">
            <div className="loading-spinner w-5 h-5 mr-2" style={{ borderWidth: 3 }} />
            Processing...
          </div>
        )}
      </div>
    );
  }

  /* ─────────────────────────────────────────────────────────────────────────
     Main budget assistant chat (bottom-left panel in live budget UI)
  ───────────────────────────────────────────────────────────────────────── */
  function renderBudgetChat() {
    return (
      <div className="w-full lg:w-1/2 bg-white rounded-xl shadow-lg p-6 h-full">
        <h2 className="text-xl font-semibold text-indigo-700 mb-4">
          Budget Assistant
          {budgetData.confidence > 0 && (
            <span className="ml-2 text-sm text-green-600 font-normal">
              {(budgetData.confidence * 100).toFixed(0)}% confidence
            </span>
          )}
        </h2>
        <div className="bg-gray-50 rounded-lg p-4 mb-4 h-80 overflow-y-auto space-y-2">
          {chatMessages.map((msg, i) => (
            <div key={i} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-sm px-4 py-2 rounded-lg text-sm whitespace-pre-wrap ${msg.sender === 'user'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-200 text-gray-800'
                  }`}
              >
                {msg.text}
              </div>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>
        <div className="relative">
          <textarea
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 h-24 resize-none"
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendPrompt(); } }}
            placeholder='Ask me to update your budget, e.g. "Update casting to 2000000"'
            disabled={isSending}
          />
          <div className="absolute bottom-3 right-3 flex space-x-2">
            <button
              onClick={() => setUploadShown(true)}
              className="p-2 bg-indigo-100 rounded-full text-indigo-700 hover:bg-indigo-200 transition-colors"
            >
              <FiUpload size={18} />
            </button>
            <button
              onClick={sendPrompt}
              disabled={isSending}
              className="p-2 bg-indigo-600 rounded-full text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              <FiSend size={18} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ─────────────────────────────────────────────────────────────────────────
     Loading screen
  ───────────────────────────────────────────────────────────────────────── */
  if (isCheckingExisting) {
    return (
      <>
        <style>{animationStyles}</style>
        <SubNav />
        <div className="loading-container">
          <div className="loading-spinner" />
          <div className="loading-text">Loading your budget workspace...</div>
          <div className="loading-sub">Checking for existing budget data</div>
        </div>
      </>
    );
  }

  /* ─────────────────────────────────────────────────────────────────────────
     No budget yet — setup screen
  ───────────────────────────────────────────────────────────────────────── */
  if (!budgetReady) {
    return (
      <>
        <style>{animationStyles}</style>
        <SubNav />
        <main className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
          <AnimatedBackground />
          <div className="max-w-2xl mx-auto pt-12">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-indigo-800 mb-2">Budget AI Planner</h1>
              <p className="text-gray-600">Let me generate a detailed production budget for your project.</p>
            </div>
            {renderSetupChat()}
          </div>
        </main>
      </>
    );
  }

  /* ─────────────────────────────────────────────────────────────────────────
     Full budget dashboard (original layout — real data)
  ───────────────────────────────────────────────────────────────────────── */
  const chartData = generateChartData();

  return (
    <>
      <SEO
        title="Film Budget Tool"
        description="Plan and manage your film's budget with ProducerBuddy's AI-powered budget tool. Break down costs by department, track spending, and generate reports."
        url="https://producerbuddy.pilomation.com/budget"
        robots="noindex, nofollow"
      />
      <style>{animationStyles}</style>
      <SubNav />
      <main className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-0">
        <AnimatedBackground />
        <div className="px-2 py-4">

          {/* ── Top row: Chat + Chart ─────────────────────────────────────── */}
          <div className="flex flex-row lg:flex-row gap-4 mb-6">

            {/* Budget assistant chat */}
            {renderBudgetChat()}

            {/* Chart */}
            <div className="w-full lg:w-1/2 bg-white rounded-xl shadow-lg p-6 flex flex-col">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-indigo-700">
                  {selectedCategory === 'total'
                    ? 'Total Budget Overview'
                    : `${formatName(selectedCategory)} Breakdown`}
                </h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => window.print()}
                    className="p-2 bg-indigo-100 rounded-full text-indigo-700 hover:bg-indigo-200"
                  >
                    <FiDownload size={18} />
                  </button>
                  <button
                    onClick={() => setShowFilterMenu(!showFilterMenu)}
                    className="p-2 bg-indigo-100 rounded-full text-indigo-700 hover:bg-indigo-200"
                  >
                    <FiFilter size={18} />
                  </button>
                </div>
              </div>

              {showFilterMenu && (
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex flex-col md:flex-row gap-3">
                    <select
                      value={chartType}
                      onChange={e => setChartType(e.target.value)}
                      className="p-2 border border-gray-300 rounded bg-white flex-1"
                    >
                      <option value="pie">Pie Chart</option>
                      <option value="bar">Bar Chart</option>
                      <option value="hist">Histogram</option>
                      <option value="line">Line Graph</option>
                    </select>
                    <select
                      value={selectedCategory}
                      onChange={e => setSelectedCategory(e.target.value)}
                      className="p-2 border border-gray-300 rounded bg-white flex-1"
                    >
                      <option value="total">Total Budget</option>
                      {Object.keys(budgetData.categories).map(k => (
                        <option key={k} value={k}>{formatName(k)}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {renderChart()}
            </div>
          </div>

          {/* ── Summary cards ─────────────────────────────────────────────── */}
          <div className="flex flex-wrap gap-4 mb-6">

            {/* Total */}
            <div className="flex-1 min-w-[250px] bg-white rounded-xl shadow-lg p-4">
              <h3 className="text-lg font-semibold text-gray-800">Total Budget</h3>
              <p className="text-2xl font-bold text-indigo-700 mt-2">{INR(budgetData.total)}</p>
            </div>

            {/* Spent */}
            <div className="flex-1 min-w-[250px] bg-white rounded-xl shadow-lg p-4 relative group">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">Spent</h3>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleEdit('spent-add')}
                    className="p-2 bg-green-100 rounded-full text-green-700 hover:bg-green-200"
                    title="Add expense"
                  >
                    <FiPlus size={16} />
                  </button>
                  <button
                    onClick={() => handleEdit('spent')}
                    className="p-2 bg-indigo-100 rounded-full text-indigo-700 hover:bg-indigo-200"
                    title="Edit spent"
                  >
                    <FiEdit2 size={16} />
                  </button>
                </div>
              </div>

              {editingCategory === 'spent' ? (
                <div className="mt-2">
                  <input
                    type="number"
                    value={inputValues.spent || ''}
                    onChange={e => handleInputChange('spent', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded"
                  />
                  <div className="flex justify-end mt-2 gap-2">
                    <button onClick={() => setEditingCategory(null)} className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
                    <button onClick={() => handleSaveEdit('spent')} className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700">Save</button>
                  </div>
                </div>
              ) : editingCategory === 'spent-add' ? (
                <div className="mt-2">
                  <input
                    type="number"
                    placeholder="Amount to add"
                    value={inputValues['spent-add'] || ''}
                    onChange={e => handleInputChange('spent-add', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded"
                  />
                  <div className="flex justify-end mt-2 gap-2">
                    <button onClick={() => setEditingCategory(null)} className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
                    <button onClick={handleAddExpense} className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700">Add</button>
                  </div>
                </div>
              ) : (
                <p className="text-2xl font-bold text-red-500 mt-2">{INR(budgetData.spent)}</p>
              )}
            </div>

            {/* Remaining */}
            <div className="flex-1 min-w-[250px] bg-white rounded-xl shadow-lg p-4">
              <h3 className="text-lg font-semibold text-gray-800">Remaining</h3>
              <p className={`text-2xl font-bold mt-2 ${budgetData.remaining >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                {INR(budgetData.remaining)}
              </p>
            </div>
          </div>

          {/* ── Add new category button ────────────────────────────────────── */}
          <div className="mb-4">
            <button
              onClick={() => handleEdit('new-category')}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
            >
              <FiPlus size={16} className="mr-2" /> Add New Category
            </button>
          </div>

          {editingCategory === 'new-category' && (
            <div className="mb-6 bg-white rounded-xl shadow-lg p-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Add New Budget Category</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Sound Equipment"
                    value={inputValues['new-category-name'] || ''}
                    onChange={e => handleInputChange('new-category-name', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Budget Amount (₹)</label>
                  <input
                    type="number"
                    placeholder="Amount in ₹"
                    value={inputValues['new-category-value'] === 0 ? '' : (inputValues['new-category-value'] || '')}
                    onChange={e => setInputValues(prev => ({
                      ...prev,
                      'new-category-value': e.target.value   // store as raw string
                    }))}
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                    min="0"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditingCategory(null)} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
                <button onClick={handleAddCategory} className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center">
                  <FiSave size={16} className="mr-2" /> Save Category
                </button>
              </div>
            </div>
          )}

          {/* ── Category cards grid ───────────────────────────────────────── */}
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-indigo-800 mb-4">Budget Breakdown</h2>

            {Object.keys(budgetData.categories).length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                No categories yet — generate a budget using the assistant above.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(budgetData.categories).map(([key, category]) => (
                  <div key={key} className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div
                      className="p-4 cursor-pointer"
                      onClick={() => setExpandedCategories(prev => ({ ...prev, [key]: !prev[key] }))}
                    >
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-800">{formatName(key)}</h3>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={e => { e.stopPropagation(); handleEdit(key); }}
                            className="p-1.5 bg-indigo-100 rounded-full text-indigo-700 hover:bg-indigo-200"
                          >
                            <FiEdit2 size={14} />
                          </button>
                          <FiChevronDown
                            size={18}
                            className={`text-gray-500 transition-transform duration-300 ${expandedCategories[key] ? 'rotate-180' : ''}`}
                          />
                        </div>
                      </div>

                      {editingCategory === key ? (
                        <div className="mt-2" onClick={e => e.stopPropagation()}>
                          <input
                            type="number"
                            value={inputValues[key] || ''}
                            onChange={e => handleInputChange(key, e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded"
                          />
                          <div className="flex justify-end mt-2 gap-2">
                            <button onClick={() => setEditingCategory(null)} className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300">Cancel</button>
                            <button onClick={() => handleSaveEdit(key)} className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700">Save</button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xl font-semibold text-indigo-600 mt-1">{INR(category.value)}</p>
                      )}
                    </div>

                    {expandedCategories[key] && (
                      <div className="px-4 pb-4 border-t border-gray-100">
                        <p className="text-gray-600 mt-2 text-sm">{category.description}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

        {/* ── Upload modal (re-generate from new script file) ─────────────── */}
        {uploadShown && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-800">Upload New Script</h3>
                <button onClick={() => setUploadShown(false)} className="text-gray-500 hover:text-gray-700"><FiX size={24} /></button>
              </div>
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-500 cursor-pointer">
                  <input
                    type="file"
                    className="hidden"
                    id="budget-file-upload"
                    accept=".pdf,.txt,.doc,.docx"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUploadShown(false);
                      addUserMsg(`📄 Uploading: ${file.name}`);
                      addBotMsg('Uploading and analyzing new script...');
                      setIsSending(true);
                      const controller = new AbortController();
                      const timeoutId = setTimeout(() => controller.abort(), 45000);

                      try {
                        const uploadFd = new FormData();
                        uploadFd.append('file', file);
                        uploadFd.append('file_type', 'document');
                        uploadFd.append('user_id', activeUser);

                        const upRes = await fetch(`${baseUrl}/upload/direct`, {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
                          body: uploadFd
                        });
                        if (!upRes.ok) throw new Error('Upload failed');
                        const upData = await upRes.json();

                        const fd = new FormData();
                        fd.append('project_id', projectId);
                        fd.append('script_file_url', upData.public_url);
                        if (conversationId) fd.append('conversation_id', conversationId);

                        const res = await fetch(`${baseUrl}/budgetConversation/generateFromManual`, {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
                          body: fd,
                          signal: controller.signal
                        });
                        if (!res.ok) throw new Error('Budget generation failed');
                        const result = await res.json();
                        onBudgetGenerated(result);
                      } catch (err) {
                        replaceBotMsg(`❌ ${err.message}`);
                      } finally {
                        clearTimeout(timeoutId);
                        setIsSending(false);
                      }
                    }}
                  />
                  <label htmlFor="budget-file-upload" className="cursor-pointer">
                    <FiUpload size={32} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600">Click to upload or drag and drop</p>
                    <p className="text-xs text-gray-500 mt-1">PDF, TXT, DOC, DOCX</p>
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

      </main>
    </>
  );
};

export default Budget;
